/**
 * @file DaysMonthsField.tsx
 * @description Пара связанных полей «…, дней» + «…, месяцев» для срока
 * годности (phase19.22, W20; замечание пользователя 2026-09-26).
 *
 * Хранится и уходит на сервер только срок в днях (`value`); месяцы — второй
 * способ ввода: дни → `days / 30` до одного знака, месяцы → `round(months × 30)`
 * (`daysToMonthsText` / `monthsTextToDays`). Пустое одно — пустое другое.
 *
 * Текст месяцев держим локально: иначе при наборе «1,» пересчёт через дни
 * тут же стёр бы запятую. Пересчитываем его из дней, только когда дни
 * поменялись не из этого поля (ввод в днях, сброс формы).
 *
 * На 360 поля идут столбиком, с sm — в строку.
 */

import { useEffect, useRef, useState } from "react";

import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { daysToMonthsText, monthsTextToDays } from "../../../lib/product-kind-format";

interface Props {
  id: string;
  /** Подпись без единицы: «Срок годности» → «Срок годности, дней / месяцев». */
  label: string;
  /** Дни строкой; "" — не задан. */
  value: string;
  onChange: (days: string) => void;
  /** Плейсхолдер дней, например «365 — из вида „Пищевой“». */
  daysPlaceholder?: string;
  /** Плейсхолдер месяцев, например «12,2 — из вида». */
  monthsPlaceholder?: string;
  maxDays: number;
  disabled?: boolean;
  invalid?: boolean;
}

export function DaysMonthsField({
  id,
  label,
  value,
  onChange,
  daysPlaceholder,
  monthsPlaceholder,
  maxDays,
  disabled,
  invalid,
}: Props) {
  const [monthsText, setMonthsText] = useState(() => daysToMonthsText(value));
  // Дни, которые мы сами выдали из поля месяцев, — их не пересчитываем обратно.
  const emittedDays = useRef<string | null>(null);

  useEffect(() => {
    if (emittedDays.current === value) return;
    emittedDays.current = null;
    setMonthsText(daysToMonthsText(value));
  }, [value]);

  function onMonthsInput(text: string) {
    setMonthsText(text);
    const days = monthsTextToDays(text);
    emittedDays.current = days;
    onChange(days);
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-days`}>{label}, дней</Label>
        <Input
          id={`${id}-days`}
          type="number"
          inputMode="numeric"
          min={1}
          max={maxDays}
          step={1}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          aria-invalid={invalid ? true : undefined}
          placeholder={daysPlaceholder}
          className="min-h-12"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-months`}>{label}, месяцев</Label>
        {/* Текстом, а не number: запятая «1,5» в number-поле теряется. */}
        <Input
          id={`${id}-months`}
          type="text"
          inputMode="decimal"
          value={monthsText}
          onChange={(e) => onMonthsInput(e.target.value)}
          disabled={disabled}
          aria-invalid={invalid ? true : undefined}
          placeholder={monthsPlaceholder}
          className="min-h-12"
        />
      </div>
    </div>
  );
}
